import { type NavigateOptions, useNavigate } from '@tanstack/react-router'
import * as React from 'react'
import { ExternalToast, Toaster as SonnerToaster, toast as toastFn } from 'sonner'

import { IconButton } from '@/components/base/icon-button'
import { useI18n } from '@/i18n/i18n-react'
import { CheckCircleSolid } from '@/icons/check-circle-solid'
import { ExclamationCircleSolid } from '@/icons/exclamation-circle-solid'
import { InformationCircleSolid } from '@/icons/information-circle-solid'
import { Spinner } from '@/icons/spinner'
import { XCircleSolid } from '@/icons/x-circle-solid'
import { XMark } from '@/icons/x-mark'
import { clx } from '@/utils/clx'

interface ToastComponentProps {
	id: string | number
	variant?: ToastVariant
	title: React.ReactNode
	description?: React.ReactNode
	action?: ToastAction
	icon?: React.ReactNode
	dismissable?: boolean
}

/**
 * This component is based on the [Sonner](https://sonner.emilkowal.ski/toast) toast library.
 */
export const Toast = ({
	/**
	 * Optional ID of the toast.
	 */
	id,
	/**
	 * @ignore
	 *
	 * @privateRemarks
	 * As the Toast component is created using
	 * the `toast` utility functions, the variant is inferred
	 * from the utility function.
	 */
	variant = 'info',
	/**
	 * @ignore
	 *
	 * @privateRemarks
	 * The `toast` utility functions accept this as a parameter.
	 */
	title,
	/**
	 * @ignore
	 *
	 * @privateRemarks
	 * The `toast` utility functions accept this as a parameter.
	 */
	icon: _icon,
	/**
	 * The toast's text.
	 */
	description,
	/**
	 * The toast's action buttons.
	 */
	action,
	/**
	 * @ignore
	 *
	 * @privateRemarks
	 * The `toast` utility functions accept this as a parameter.
	 */
	dismissable = true
}: ToastComponentProps) => {
	let icon = _icon

	if (!_icon) {
		switch (variant) {
			case 'success':
				icon = <CheckCircleSolid className='text-ui-tag-green-icon' />
				break
			case 'warning':
				icon = <ExclamationCircleSolid className='text-ui-tag-orange-icon' />
				break
			case 'error':
				icon = <XCircleSolid className='text-ui-tag-red-icon' />
				break
			case 'loading':
				icon = <Spinner className='animate-spin text-ui-tag-blue-icon' />
				break
			case 'info':
				icon = <InformationCircleSolid className='text-ui-fg-base' />
				break
			default:
				break
		}
	}

	return (
		<div className='flex w-fit min-w-[360px] max-w-[440px] gap-x-3 overflow-hidden rounded-lg bg-ui-bg-component p-3 shadow-elevation-flyout'>
			<div
				className={clx('grid flex-1 items-center gap-x-2', {
					'grid-cols-[20px_1fr]': !!icon,
					'grid-cols-1': !icon,
					'items-start': !!description
				})}
			>
				{!!icon && (
					<span className='flex size-5 items-center justify-center' aria-hidden>
						{icon}
					</span>
				)}
				<div className='flex flex-col gap-y-3'>
					<div className='flex flex-col'>
						<span className='txt-compact-small-plus text-ui-fg-base'>{title}</span>
						{description && <span className='txt-small text-pretty text-ui-fg-subtle'>{description}</span>}
					</div>
					{!!action && (
						<button
							type='button'
							className={clx(
								'txt-compact-small-plus flex w-fit items-center rounded-[4px] bg-ui-bg-base text-ui-fg-base transition-colors',
								'focus-visible:shadow-borders-focus',
								'hover:text-ui-fg-subtle',
								'disabled:text-ui-fg-disabled',
								{
									'text-ui-fg-error': action.variant === 'destructive'
								}
							)}
							onClick={action.onClick}
						>
							{action.label}
						</button>
					)}
				</div>
			</div>
			{!!dismissable && (
				<IconButton size='2xsmall' variant='transparent' type='button' onClick={() => toast.dismiss(id)}>
					<XMark />
				</IconButton>
			)}
		</div>
	)
}

export type ToasterPosition = 'top-left' | 'top-center' | 'top-right' | 'bottom-left' | 'bottom-center' | 'bottom-right'

export type ToastVariant = 'info' | 'success' | 'warning' | 'error' | 'loading' | 'message'

export type ToastActionVariant = 'default' | 'destructive'

export type ToastAction = {
	/**
	 * The button's text.
	 */
	label: string
	/**
	 * The button's alt text.
	 */
	altText: string
	/**
	 * The function to execute when the button is clicked.
	 */
	onClick: () => void | Promise<void>
	/**
	 * The button's variant.
	 */
	variant?: ToastActionVariant
}

interface BaseToastProps {
	id?: string | number
	position?: ToasterPosition
	duration?: number
	dismissable?: boolean
	icon?: React.ReactNode
}

interface ToastProps extends BaseToastProps {
	description?: React.ReactNode
	action?: ToastAction
}

function create(variant: ToastVariant, title: React.ReactNode, props: ToastProps = {}) {
	const external: ExternalToast = {
		position: props.position,
		duration: props.duration,
		dismissible: props.dismissable
	}

	if (props.id) {
		external.id = props.id
	}

	return toastFn.custom(t => {
		return (
			<Toast
				id={t}
				title={title}
				description={props.description}
				dismissable={props.dismissable}
				variant={variant}
				action={props.action}
				icon={props.icon}
			/>
		)
	}, external)
}

function message(
	/**
	 * The title of the toast.
	 */
	title: string,
	/**
	 * The props of the toast.
	 */
	props: ToastProps = {}
) {
	return create('message', title, props)
}

function custom() {
	return create('message', 'Custom')
}

interface VariantToastProps extends Omit<ToastProps, 'icon'> {}

function info(
	/**
	 * The title of the toast.
	 */ title: string,
	/**
	 * The props of the toast.
	 */
	props: VariantToastProps = {}
) {
	return create('info', title, props)
}

function error(
	/**
	 * The title of the toast.
	 */ title: string,
	/**
	 * The props of the toast.
	 */
	props: VariantToastProps = {}
) {
	return create('error', title, props)
}

function success(
	/**
	 * The title of the toast.
	 */ title: string,
	/**
	 * The props of the toast.
	 */
	props: VariantToastProps = {}
) {
	return create('success', title, props)
}

function warning(
	/**
	 * The title of the toast.
	 */ title: string,
	/**
	 * The props of the toast.
	 */
	props: VariantToastProps = {}
) {
	return create('warning', title, props)
}

function loading(
	/**
	 * The title of the toast.
	 */ title: string,
	/**
	 * The props of the toast.
	 */
	props: VariantToastProps = {}
) {
	return create('loading', title, { ...props, dismissable: false })
}

type PromiseStateProps =
	| string
	| {
			title: string
			description?: string
	  }

interface PromiseToastProps extends Omit<BaseToastProps, 'icon'> {
	loading: PromiseStateProps
	success: PromiseStateProps
	error: PromiseStateProps
}

function createUniqueID() {
	return Math.random().toString(36).slice(2, 8)
}

async function promise<TData>(
	/**
	 * The promise to be resolved.
	 */
	promise: Promise<TData> | (() => Promise<TData>),
	/**
	 * The props of the toast.
	 */
	props: PromiseToastProps
) {
	let id: string | number | undefined = props.id || createUniqueID()
	let shouldDismiss = id !== undefined

	id = create('loading', typeof props.loading === 'string' ? props.loading : props.loading.title, {
		id: id,
		position: props.position,
		description: typeof props.loading === 'string' ? undefined : props.loading.description,
		duration: Infinity,
		dismissable: false
	})

	const p = promise instanceof Promise ? promise : promise()

	p.then(() => {
		shouldDismiss = false
		create('success', typeof props.success === 'string' ? props.success : props.success.title, {
			id: id,
			position: props.position,
			description: typeof props.success === 'string' ? undefined : props.success.description
		})
	})
		.catch(() => {
			shouldDismiss = false
			create('error', typeof props.error === 'string' ? props.error : props.error.title, {
				id: id,
				position: props.position,
				description: typeof props.error === 'string' ? undefined : props.error.description
			})
		})
		.finally(() => {
			if (shouldDismiss) {
				toastFn.dismiss(id)
				id = undefined
			}
		})

	return id
}

export const toast = Object.assign(message, {
	info,
	error,
	warning,
	success,
	promise,
	loading,
	dismiss: toastFn.dismiss
})

export function Toaster() {
	return <SonnerToaster position='bottom-right' />
}

/**
 * Success toast with a "View" action that navigates to the created entity's detail route.
 */
export function useCreatedToast() {
	const { L } = useI18n()
	const navigate = useNavigate()

	return (title: string, to: NavigateOptions, props: VariantToastProps = {}) =>
		toast.success(title, {
			...props,
			action: {
				label: L.General.View(),
				altText: L.General.View(),
				onClick: () => navigate(to)
			}
		})
}
